export interface ModalHandle {
  close: () => void;
  bodyEl: HTMLElement;
}

export interface ModalOptions {
  /**
   * Fired exactly once when the modal goes away — backdrop click, Escape, the ✕, or a programmatic
   * `close()` alike. Callers that must settle something (a promise waiting on the user's answer)
   * hang it here so dismissing rather than confirming still resolves.
   */
  onClose?: () => void;
}

/** Opens a simple overlay modal with a title + close button; closes on backdrop click or Escape. */
export function openModal(title: string, options: ModalOptions = {}): ModalHandle {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';

  const modal = document.createElement('div');
  modal.className = 'modal';

  const header = document.createElement('div');
  header.className = 'modal-header';
  const titleEl = document.createElement('span');
  titleEl.textContent = title;
  header.appendChild(titleEl);
  const closeBtn = document.createElement('button');
  closeBtn.className = 'btn subtle modal-close';
  closeBtn.textContent = '✕';
  closeBtn.setAttribute('aria-label', 'Close');
  header.appendChild(closeBtn);
  modal.appendChild(header);

  const body = document.createElement('div');
  body.className = 'modal-body';
  modal.appendChild(body);

  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') close();
  }

  let closed = false;
  function close() {
    if (closed) return;
    closed = true;
    document.removeEventListener('keydown', onKeydown);
    overlay.remove();
    options.onClose?.();
  }

  document.addEventListener('keydown', onKeydown);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  closeBtn.addEventListener('click', close);

  return { close, bodyEl: body };
}
